import * as React from 'react';
import {ProvidedGlobalProps, withGlobalProps} from '../../providers/globalPropsProvider';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import classNames from 'classnames';
import s from './ProductPageButtonsContainer.scss';
import {Cell} from '../Layouts/Cell/Cell';
import {AddToCartButton} from './AddToCartButton/AddToCartButton';
import {Mode, WishlistButton} from './WishlistButton/WishlistButton';
import _ from 'lodash';
import {BuyNowButton} from './BuyNowButton/BuyNowButton';
import {inStock} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import {ShowOnDesktopOnly} from '@wix/wixstores-client-common-components/dist/es/src/HOC/responsive/ShowOnDesktopOnly/ShowOnDesktopOnly';
import {SubscribeButton} from './SubscribeButton/SubscribeButton';
import {CashierExpressButton} from './CashierExpressButton/CashierExpressButton';
import {AddToCartState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/AddToCartService/constants';
import {BackInStockButton} from './BackInStockButton/BackInStockButton';
import {PreOrderMessage} from './PreOrderMessage/PreOrderMessage';
import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {ProductPageSlotIds} from '../../constants';
import {ConditionalRender} from '../ConditionalRender/ConditionalRender';

export interface ProductPageButtonsContainerProps extends ProvidedGlobalProps, IProvidedTranslationProps {}

export enum ProductPageButtonContainerDataHook {
  firstRow = 'buy-now-button-container-first-row',
  secondRow = 'buy-now-button-container',
  wishlistContainer = 'wishlist-container',
  cashierButtonContainer = 'cashier-button-container',
  backInStockErrorContainer = 'back-in-stock-error-container',
}

class ProductPageButtonsContainerComponent extends React.Component<ProductPageButtonsContainerProps> {
  private readonly handleAddToCart = async () => {
    const {handleAddToCart} = this.props.globals;

    /* istanbul ignore if: todo(ariel): test ssr protection */
    _.isFunction(handleAddToCart) && (await handleAddToCart());
  };

  private readonly isAddToCartDisabled = (): boolean => {
    const {addToCartState} = this.props.globals;
    return addToCartState !== AddToCartState.ENABLED;
  };

  private readonly BuyNowButtonSecondRowContainer = () => {
    const {
      globals: {dynamicPaymentsMethodsAmount, product, selectedVariant},
    } = this.props;
    const productInStock = inStock(product, selectedVariant);
    const hasNoDynamicPaymentMethod = dynamicPaymentsMethodsAmount <= 0 || dynamicPaymentsMethodsAmount === undefined;
    const secondRowClasses = classNames(s.productOptionsContainer, s.secondRow);

    return (
      <ConditionalRender by="productPage_productAction" className={s.fullWidth}>
        {hasNoDynamicPaymentMethod && (
          <ConditionalRender by="productPage_dynamicPaymentMethodsEnabled" className={s.fullWidth}>
            <Cell dataHook={ProductPageButtonContainerDataHook.secondRow} className={secondRowClasses}>
              <BuyNowButton productInStock={productInStock} />
            </Cell>
          </ConditionalRender>
        )}
        <ConditionalRender by="shouldRenderBuyNowButtonWithDynamicPaymentMethodDisabled" className={s.fullWidth}>
          <Cell dataHook={ProductPageButtonContainerDataHook.secondRow} className={secondRowClasses}>
            <BuyNowButton productInStock={productInStock} />
          </Cell>
        </ConditionalRender>
      </ConditionalRender>
    );
  };

  private readonly CashierButtonContainer = () => {
    const {
      globals: {dynamicPaymentsMethodsAmount},
    } = this.props;
    const secondRowClasses = classNames(s.productOptionsContainer, s.secondRow);

    return (
      <ConditionalRender by="productPage_dynamicPaymentMethodsEnabled" className={s.fullWidth}>
        {!this.isAddToCartDisabled() && (
          <ConditionalRender by="productPage_productAction" className={s.fullWidth}>
            <Cell dataHook={ProductPageButtonContainerDataHook.secondRow} className={secondRowClasses}>
              <div
                data-hook={ProductPageButtonContainerDataHook.cashierButtonContainer}
                className={
                  /* istanbul ignore next: todo: test */
                  dynamicPaymentsMethodsAmount === undefined ? s.invisible : ''
                }
                data-wix-hideable={true}>
                <CashierExpressButton />
              </div>
            </Cell>
          </ConditionalRender>
        )}
        <ConditionalRender by="shouldRenderCashierButtonWithAddToCartDisabled" className={s.fullWidth}>
          <Cell dataHook={ProductPageButtonContainerDataHook.secondRow} className={secondRowClasses}>
            <div
              data-hook={ProductPageButtonContainerDataHook.cashierButtonContainer}
              className={
                /* istanbul ignore next: todo: test */
                this.isAddToCartDisabled() || dynamicPaymentsMethodsAmount === undefined ? s.invisible : ''
              }
              data-wix-hideable={true}>
              <CashierExpressButton />
            </div>
          </Cell>
        </ConditionalRender>
      </ConditionalRender>
    );
  };

  private renderBuyNowAndCashierContainer() {
    const {
      globals: {dynamicPaymentsMethodsAmount},
    } = this.props;
    const hasDynamicPaymentMethod = dynamicPaymentsMethodsAmount > 0 || dynamicPaymentsMethodsAmount === undefined;

    return (
      <ConditionalRender by="productPage_buyNowButtonEnabled" className={s.fullWidth}>
        {!this.isAddToCartDisabled() && this.BuyNowButtonSecondRowContainer()}
        {hasDynamicPaymentMethod && this.CashierButtonContainer()}
      </ConditionalRender>
    );
  }

  public renderSubscribeButtonInFirstRow({buttonContainerClass}: {buttonContainerClass: string}) {
    return (
      <ConditionalRender by="shouldShowSubscribeInFirstRow" className={buttonContainerClass}>
        <SubscribeButton productInStock={true} />
      </ConditionalRender>
    );
  }

  public renderSubscribeButtonInSecondRow() {
    return (
      <ConditionalRender by="shouldShowSubscribeSecondRow">
        <Cell className={s.secondRow}>
          <SubscribeButton productInStock={true} />
        </Cell>
      </ConditionalRender>
    );
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public render(): JSX.Element {
    const {
      globals: {
        selectedVariant,
        product,
        shouldShowWishlistButton,
        shouldShowSubscribeButton,
        isBackInStockEnabled,
        isPreOrderState,
        experiments: {renderProductPageSlots, showAddToCartWithSubscription},
        slots,
      },
    } = this.props;
    const buttonContainerClass = classNames(s.productOptionsContainer, {
      [s.addToCartButtonContainer]: shouldShowWishlistButton,
    });

    const wishlistButtonContainer = classNames({
      [s.wishlistContainer]: !shouldShowSubscribeButton,
    });

    const productInStock = inStock(product, selectedVariant);
    const shouldShowBackInStockButton = isBackInStockEnabled && !productInStock && !isPreOrderState;

    const shouldPresentPreOrderMessage =
      isPreOrderState && (!!product.inventory.preOrderInfoView.message || !!selectedVariant?.preOrderInfo?.message);
    const isPluginInstalledInAdditionalButtonsSlot = !!slots?.[ProductPageSlotIds.ProductPageAdditionalButtons];
    const shouldShowNewSubscribeButton =
      !shouldShowBackInStockButton && shouldShowSubscribeButton && showAddToCartWithSubscription;
    const isOutOfStockSubscription = shouldShowNewSubscribeButton && !productInStock;
    const shouldShowAddToCartButton =
      !shouldShowBackInStockButton &&
      (!shouldShowSubscribeButton || (showAddToCartWithSubscription && !isOutOfStockSubscription));

    return (
      <>
        {shouldPresentPreOrderMessage ? <PreOrderMessage /> : null}
        <Cell className={buttonContainerClass} data-hook={ProductPageButtonContainerDataHook.firstRow}>
          {shouldShowBackInStockButton && <BackInStockButton />}

          {!shouldShowBackInStockButton && shouldShowSubscribeButton && !showAddToCartWithSubscription && (
            <SubscribeButton productInStock={productInStock} />
          )}

          {shouldShowNewSubscribeButton &&
            productInStock &&
            this.renderSubscribeButtonInFirstRow({buttonContainerClass})}

          {isOutOfStockSubscription && <SubscribeButton productInStock={false} />}
          {shouldShowAddToCartButton && (
            <ConditionalRender by="productPage_productAction" className={s.addToCartButtonContainer}>
              <AddToCartButton productInStock={productInStock} handleAddToCart={this.handleAddToCart} />
            </ConditionalRender>
          )}

          {!shouldShowSubscribeButton && !shouldShowBackInStockButton && (
            <ConditionalRender by="shouldRenderBuyNowButtonInFirstRow" className={s.fullWidth}>
              <BuyNowButton productInStock={productInStock} />
            </ConditionalRender>
          )}

          {shouldShowWishlistButton && (
            <ShowOnDesktopOnly
              className={wishlistButtonContainer}
              data-hook={ProductPageButtonContainerDataHook.wishlistContainer}>
              <WishlistButton mode={Mode.INLINE} />
            </ShowOnDesktopOnly>
          )}
        </Cell>
        {shouldShowNewSubscribeButton && productInStock && this.renderSubscribeButtonInSecondRow()}
        {!shouldShowSubscribeButton && !isPreOrderState && (
          <div className={s.buyNowAndCashierContainer}>{this.renderBuyNowAndCashierContainer()}</div>
        )}
        {renderProductPageSlots && (
          <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInAdditionalButtonsSlot})}>
            <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageAdditionalButtons} />
          </Cell>
        )}
      </>
    );
  }
}

export const ProductPageButtonsContainer = withGlobalProps(
  withTranslations('globals.texts')(ProductPageButtonsContainerComponent)
);
